import React, { useEffect, useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import { Select } from "../components/UI";
import {
  apiHeaders,
  formatDate,
  getCachedStripeUser,
  getStripeUserByEmail,
  useToast,
} from "../hooks";

const credits = [
  { label: "20 Credits", value: 20, type: 1 },
  { label: "50 Credits", value: 50, type: 2 },
  { label: "100 Credits", value: 100, type: 3 },
  { label: "200 Credits", value: 200, type: 4 },
  { label: "300 Credits", value: 300, type: 5 },
  { label: "400 Credits", value: 400, type: 6 },
  { label: "500 Credits", value: 500, type: 7 },
];

export default function TopUp() {
  const cachedUser = AuthService.getCachedUserAttributes?.() || null;
  const [user, setUser] = useState(cachedUser);
  const [detailedUser, setDetailedUser] = useState(
    cachedUser?.username ? getCachedStripeUser(cachedUser.username) : null,
  );
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const addToast = useToast();
  const creditValue = selectedCredit?.value || 0;
  const totalAmount = creditValue * 5;
  const availableCredits = Number(detailedUser?.screening || 0);
  const freeCredits = Number(detailedUser?.freeScreening || 0);
  const memberSince = formatDate(detailedUser?.createdDate);

  useEffect(() => {
    async function load() {
      const authenticated =
        await AuthService.currentAuthenticatedUserAttributes();
      setUser(authenticated);
      const cachedDetailed = getCachedStripeUser(authenticated.username);
      if (cachedDetailed) {
        setDetailedUser(cachedDetailed);
      }
      const freshDetailed = await getStripeUserByEmail(authenticated.username, {
        forceRefresh: !cachedDetailed,
      });
      setDetailedUser(freshDetailed || cachedDetailed || null);

      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get("payment");
      const sessionId = urlParams.get("session_id");

      if (paymentStatus === "success") {
        let creditApplied = false;
        let addedCredits = null;

        if (sessionId) {
          for (let attempt = 0; attempt < 6; attempt += 1) {
            try {
              const response = await axios.get(
                `/v1/integrations/stripe/stripe/checkout-session/${encodeURIComponent(sessionId)}`,
                { headers: apiHeaders },
              );
              const payload = response?.data || {};
              if (payload.creditApplied) {
                creditApplied = true;
                addedCredits = payload.creditsAdded || null;
                break;
              }
            } catch (error) {
              if (attempt === 0) {
                console.error("Failed to load checkout session status:", error);
              }
            }

            await new Promise((resolve) => {
              window.setTimeout(resolve, 1500);
            });
          }
        }

        const freshAfterPayment = await getStripeUserByEmail(
          authenticated.username,
          { forceRefresh: true },
        );
        if (freshAfterPayment) {
          setDetailedUser(freshAfterPayment);
        }

        if (creditApplied) {
          addToast({
            severity: "success",
            summary: "Payment Successful",
            detail: addedCredits
              ? `${addedCredits} credits were added to your account.`
              : "Credits were added to your account.",
            life: 10000,
          });
        } else {
          addToast({
            severity: "info",
            summary: "Payment Received",
            detail:
              "Your payment is confirmed. Credits are being applied and will appear shortly.",
            life: 10000,
          });
        }
      }

      if (paymentStatus === "error") {
        addToast({
          severity: "error",
          summary: "Error",
          detail: "Somethings went wrong about payment. Contact us!",
          life: 10000,
        });
      }

      if (paymentStatus) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    }
    load();
  }, [addToast]);

  const topUp = async () => {
    if (isProcessing || !selectedCredit || !user) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(
        "/v1/integrations/stripe/stripe/create-payment-intent",
        { type: selectedCredit.type, email: user.username },
        { headers: apiHeaders },
      );
      window.location.href = res.data.url;
    } catch (error) {
      console.error("Top up checkout creation failed:", error);
      addToast({
        severity: "error",
        summary: "Payment Error",
        detail:
          error?.response?.data?.message ||
          "Unable to start Stripe checkout. Please try again.",
        life: 10000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="topup-page-modern">
      <div className="topup-modern-head">
        <p>Credits</p>
        <h1>Top Up Credits</h1>
        <span>Select the number of credits and continue to checkout.</span>
      </div>
      <section className="topup-shell">
        <div className="topup-builder-card">
          <div className="topup-builder-head">
            <div>
              <p>Price per credit</p>
              <strong>$5.00</strong>
            </div>
            <span className="topup-builder-icon">
              <i className="pi pi-credit-card" aria-hidden="true" />
            </span>
          </div>
          <div className="input-container topup-select-wrap topup-modern-select">
            <label className="form-label">Number of credits</label>
            <Select
              value={selectedCredit}
              onChange={setSelectedCredit}
              options={credits}
              labelKey="label"
              valueKey="value"
              placeholder="Select Top Up Option"
              disabled={isProcessing}
            />
          </div>
          <div className="topup-total-row">
            <div>
              <span>Total amount</span>
              <small>{creditValue} credits x $5.00</small>
            </div>
            <strong>${totalAmount.toFixed(2)}</strong>
          </div>
          <button
            type="button"
            className={`topup-buy-btn ${selectedCredit ? "" : "disabled"}`}
            onClick={selectedCredit ? topUp : undefined}
            disabled={isProcessing || !selectedCredit || !detailedUser}
          >
            <i className="pi pi-credit-card" aria-hidden="true" />
            {isProcessing
              ? "Redirecting..."
              : `Continue to checkout · $${totalAmount.toFixed(2)}`}
          </button>
          <div className="topup-secure-note">
            <i className="pi pi-verified" aria-hidden="true" />
            <span>
              Securely processed by Stripe. Trustd.ai does not collect or store
              your sensitive payment information.
            </span>
          </div>
        </div>

        <aside className="topup-side-stack">
          <div className="topup-side-card">
            <h3>Account Snapshot</h3>
            <div className="topup-stat-row">
              <span>Available Credits</span>
              <strong>{detailedUser ? availableCredits : "..."}</strong>
            </div>
            <div className="topup-stat-row">
              <span>Member Since</span>
              <strong>{memberSince}</strong>
            </div>
          </div>
          {selectedCredit ? (
            <div className="topup-after-card">
              <p>After top-up</p>
              <strong>
                {detailedUser ? availableCredits + creditValue : "..."}
              </strong>
              <span>total credits</span>
              <small>+{creditValue} credits added</small>
            </div>
          ) : null}
          <div className="topup-help-card">
            <p>How credits work</p>
            <span>
              <i className="pi pi-verified" aria-hidden="true" />1 credit = 1
              guest screening
            </span>
            <span>
              <i className="pi pi-verified" aria-hidden="true" />
              Credits expire 12 months after the date of purchase
            </span>
          </div>
        </aside>
      </section>

      <div className="sales-contact topup-sales-modern">
        Need More Than 500 Screening Credits? Contact{" "}
        <a href="mailto:sales@trustd.ai">sales@trustd.ai</a>
      </div>
    </div>
  );
}
